import React from "react";
import PropTypes from "prop-types";
import { isSubscriptionMember, isClassicLens } from "helpers/membership-helpers";
import { compose } from "redux";
import MembershipPickWayfind from "components/source/membership/membership-pick-wayfind";
import SaleableFormFields from "components/source/reservations/saleable-form-fields";
import RentableFormFields from "components/source/reservations/rentable-form-fields";
import MembershipFormFields from "components/source/membership/membership-form-fields";
import { analytics } from "rtr-constants";
import { browserPropType, membershipStatePropType, PDPContentPropType } from "components/propTypes";
import { getQueryParam } from "helpers/location-helpers";
import PdpAddToBagTabs from "../products/pdp-atb-tabs";
import {
  flagsAndExperimentsPropType,
  flagsAndExperimentNames,
  withOnLoadFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";

class ReservationFormFields extends React.Component {
  static propTypes = {
    analyticsLoggingContext: PropTypes.object,
    browser: browserPropType,
    cmsContent: PDPContentPropType,
    deliveryCutoffs: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    displayModal: PropTypes.func,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errors: PropTypes.object,
    flagsAndExperiments: flagsAndExperimentsPropType,
    gridProductDrawer: PropTypes.object,
    handleWayfindCtaClick: PropTypes.func,
    hasLengths: PropTypes.bool,
    isBuyNow: PropTypes.bool,
    isHomepageDrawer: PropTypes.bool,
    isOneSize: PropTypes.bool,
    isRentable: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    logOpenAvailability: PropTypes.func,
    membershipBag: PropTypes.object,
    membershipState: membershipStatePropType,
    narrow: PropTypes.bool,
    offerBackupSize: PropTypes.bool,
    onAddToBagFailure: PropTypes.func,
    onChange: PropTypes.func,
    // onChangeReservationInput is used as the onChange func
    // for new animated input fields. The function takes
    // four arguments - value, isValid, name, inputObj
    onChangeReservationInput: PropTypes.func,
    product: PropTypes.object.isRequired,
    recommendations: PropTypes.arrayOf(PropTypes.shape),
    remaining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reservation: PropTypes.object,
    selectedSku: PropTypes.shape({
      size: PropTypes.string,
    }),
    canRenderSplitTheBusiness: PropTypes.bool,
    updateReservation: PropTypes.func,
    userData: PropTypes.object,
    workingFilters: PropTypes.object,
    isSelectedSizeAvailable: PropTypes.func,
  };

  renderMembershipPickWayfind = () => {
    return isSubscriptionMember(this.props.userData) ? (
      <MembershipPickWayfind
        handleWayfindCtaClick={this.props.handleWayfindCtaClick}
        isMobileViewport={this.props.browser.isMobileViewport}
        membershipState={this.props.membershipState}
        product={this.props.product}
        userData={this.props.userData}
      />
    ) : null;
  };

  renderSaleableFormFields = () => {
    return (
      <SaleableFormFields
        browser={this.props.browser}
        errors={this.props.errors}
        isOneSize={this.props.isOneSize}
        isRentable={this.props.isRentable}
        isSubmitting={this.props.isSubmitting}
        onChange={this.props.onChangeReservationInput}
        product={this.props.product}
        recommendations={this.props.recommendations}
        remaining={this.props.remaining}
        reservation={this.props.reservation}
        selectedSku={this.props.selectedSku}
      />
    );
  };

  renderMembershipFormFields = () => {
    const { onAddToBagFailure, gridProductDrawer } = this.props;
    return (
      <section className="membership-form-fields-wrapper relative">
        <MembershipFormFields
          analyticsLoggingContext={this.props.analyticsLoggingContext}
          dispatch={this.props.dispatch}
          displayedModal={this.props.displayedModal}
          handleWayfindCtaClick={this.props.handleWayfindCtaClick}
          isBuyNow={this.props.isBuyNow || getQueryParam("buynow") === "true" || getQueryParam("buyNow") === "true"} // this.props.isBuynow returns undefined off first mount
          isHomepageDrawer={this.props.isHomepageDrawer}
          isSubmittingMembershipActions={this.props.membershipState?.isSubmittingMembershipActions ?? false}
          loggingLocation={gridProductDrawer ? analytics.ACTION_LOCATIONS.DRAWER : analytics.ACTION_LOCATIONS.PDP}
          membershipBag={this.props.membershipBag}
          membershipState={this.props.membershipState}
          membershipToggleRequired={true}
          onAddToBagFailure={onAddToBagFailure}
          product={this.props.product}
          userData={this.props.userData}
          workingFilters={this.props.workingFilters}
          isSelectedSizeAvailable={this.props.isSelectedSizeAvailable}
        />
      </section>
    );
  };

  renderRentableFormFields = () => {
    return (
      <section className="rentable-form-fields-wrapper">
        <RentableFormFields
          browser={this.props.browser}
          cmsContent={this.props.cmsContent}
          deliveryCutoffs={this.props.deliveryCutoffs}
          dispatch={this.props.dispatch}
          displayModal={this.props.displayModal}
          displayedModal={this.props.displayedModal}
          errors={this.props.errors}
          hasLengths={this.props.hasLengths}
          handleWayfindCtaClick={this.props.handleWayfindCtaClick}
          isOneSize={this.props.isOneSize}
          isRentable={this.props.isRentable}
          isSubmitting={this.props.isSubmitting}
          canRenderSplitTheBusiness={this.props.canRenderSplitTheBusiness}
          logOpenAvailability={this.props.logOpenAvailability}
          narrow={this.props.narrow}
          offerBackupSize={this.props.offerBackupSize}
          onChange={this.props.onChange}
          onChangeReservationInput={this.props.onChangeReservationInput}
          product={this.props.product}
          remaining={this.props.remaining}
          reservation={this.props.reservation}
          selectedSku={this.props.selectedSku}
          updateReservation={this.props.updateReservation}
        />
        {this.renderMembershipPickWayfind()}
      </section>
    );
  };

  render = () => {
    const { isRentable, userData, reservation, handleWayfindCtaClick, product } = this.props;

    if (!isRentable) {
      return this.renderSaleableFormFields();
    }

    if (this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_1494_ATB_TABS]) {
      return (
        <PdpAddToBagTabs
          renderRentableFormFields={this.renderRentableFormFields}
          renderMembershipFormFields={this.renderMembershipFormFields}
          userData={userData}
          product={product}
          reservation={reservation}
          handleWayfindCtaClick={handleWayfindCtaClick}
        />
      );
    }

    if (isClassicLens(userData)) {
      return this.renderRentableFormFields();
    }

    return this.renderMembershipFormFields();
  };
}

export default compose(withOnLoadFlagsAndExperiments(flagsAndExperimentNames.RSV_1494_ATB_TABS))(ReservationFormFields);
